// src/components/AdvancedChart.jsx
// lightweight-charts v5 — EMA20/50/200, RSI, MACD, infinite-scroll support
import React, { useRef, useEffect, useCallback } from 'react';
import {
  createChart,
  CrosshairMode,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
} from 'lightweight-charts';

// ── Indicator math ────────────────────────────────────────────────────────────

function calcEMA(values, period) {
  const k   = 2 / (period + 1);
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < period && i < values.length; i++) sum += values[i];
  if (values.length < period) return out;
  out[period - 1] = sum / period;
  for (let i = period; i < values.length; i++)
    out[i] = values[i] * k + out[i - 1] * (1 - k);
  return out;
}

function calcRSI(closes, period = 14) {
  const out = new Array(closes.length).fill(null);
  if (closes.length <= period) return out;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const d = closes[i] - closes[i - 1];
    if (d > 0) gains += d; else losses -= d;
  }
  let avgG = gains / period, avgL = losses / period;
  out[period] = 100 - 100 / (1 + (avgL === 0 ? 1e9 : avgG / avgL));
  for (let i = period + 1; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1];
    const g = d > 0 ? d : 0, l = d < 0 ? -d : 0;
    avgG = (avgG * (period - 1) + g) / period;
    avgL = (avgL * (period - 1) + l) / period;
    out[i] = 100 - 100 / (1 + (avgL === 0 ? 1e9 : avgG / avgL));
  }
  return out;
}

function calcMACD(closes, fast = 12, slow = 26, sig = 9) {
  const ef = calcEMA(closes, fast);
  const es = calcEMA(closes, slow);
  const macd = closes.map((_, i) =>
    ef[i] !== null && es[i] !== null ? ef[i] - es[i] : null
  );
  // signal = EMA(macd, 9) — computed over non-null values then re-mapped
  const vals = macd.filter(v => v !== null);
  const sigArr = calcEMA(vals, sig);
  let si = 0;
  const signal = macd.map(v => (v !== null ? (sigArr[si++] ?? null) : null));
  const hist   = macd.map((v, i) => v !== null && signal[i] !== null ? v - signal[i] : null);
  return { macd, signal, hist };
}

// ── AdvancedChart ─────────────────────────────────────────────────────────────

export default function AdvancedChart({
  data,
  height     = 500,
  showVolume = true,
  showEMA    = false,
  showRSI    = false,
  showMACD   = false,
  onLoadMore = null,   // callback(oldestTimestamp) — triggered when user scrolls to left edge
}) {
  const containerRef  = useRef(null);
  const chartRef      = useRef(null);
  const seriesRefs    = useRef({});
  const initialLoad   = useRef(true);
  const loadingMore   = useRef(false);

  // ── Reset loadingMore guard when new data arrives ─────────────────────────
  useEffect(() => { loadingMore.current = false; }, [data]);

  // ── Effect 1 — (re)create chart + series when INDICATOR toggles change ────
  useEffect(() => {
    if (!containerRef.current) return;

    // Tear down previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
    seriesRefs.current = {};
    initialLoad.current = true;

    // ── Panel layout math ─────────────────────────────────────────────────
    // Build ordered list of sub-panels below the main price chart.
    const subPanels   = [showVolume && 'vol', showRSI && 'rsi', showMACD && 'macd'].filter(Boolean);
    const nPanels     = subPanels.length;
    const PANEL_H     = 0.18;                     // each sub-panel = 18% of chart height
    const bottomMargin = nPanels * PANEL_H;        // total space reserved at bottom

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height,
      layout: { background: { color: '#131722' }, textColor: '#D9D9D9' },
      grid:   { vertLines: { color: '#1e2332' }, horzLines: { color: '#1e2332' } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderColor:  '#2a2e39',
        scaleMargins: { top: 0.05, bottom: bottomMargin + 0.01 },
      },
      timeScale: { borderColor: '#2a2e39', timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    // ── Candlestick ───────────────────────────────────────────────────────
    seriesRefs.current.candles = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderVisible: false, wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });

    // ── EMA overlays (on main pane) ───────────────────────────────────────
    if (showEMA) {
      seriesRefs.current.ema20  = chart.addSeries(LineSeries, { color: '#2196F3', lineWidth: 1, title: 'EMA 20' });
      seriesRefs.current.ema50  = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 1, title: 'EMA 50' });
      seriesRefs.current.ema200 = chart.addSeries(LineSeries, { color: '#FF5722', lineWidth: 2, title: 'EMA 200' });
    }

    // ── Sub-panel series — assign priceScaleId + scale margins ───────────
    subPanels.forEach((id, idx) => {
      // Non-overlapping slot:  top = 1 - (nPanels - idx) * PANEL_H
      //                        bottom = (nPanels - idx - 1) * PANEL_H
      const top    = 1 - (nPanels - idx) * PANEL_H;
      const bottom = (nPanels - idx - 1) * PANEL_H + (idx < nPanels - 1 ? 0.01 : 0);

      if (id === 'vol') {
        seriesRefs.current.vol = chart.addSeries(HistogramSeries, {
          priceScaleId: 'vol',
          color:        '#26a69a',
          priceFormat:  { type: 'volume' },
        });
        chart.priceScale('vol').applyOptions({ scaleMargins: { top, bottom } });
      }

      if (id === 'rsi') {
        seriesRefs.current.rsi   = chart.addSeries(LineSeries, { color: '#9C27B0', lineWidth: 1, priceScaleId: 'rsi', title: 'RSI 14' });
        seriesRefs.current.rsiOB = chart.addSeries(LineSeries, { color: '#ef535055', lineWidth: 1, priceScaleId: 'rsi', lineStyle: 2 });
        seriesRefs.current.rsiOS = chart.addSeries(LineSeries, { color: '#26a69a55', lineWidth: 1, priceScaleId: 'rsi', lineStyle: 2 });
        chart.priceScale('rsi').applyOptions({ scaleMargins: { top, bottom } });
      }

      if (id === 'macd') {
        seriesRefs.current.macdLine = chart.addSeries(LineSeries,      { color: '#2196F3', lineWidth: 1, priceScaleId: 'macd', title: 'MACD' });
        seriesRefs.current.macdSig  = chart.addSeries(LineSeries,      { color: '#FF9800', lineWidth: 1, priceScaleId: 'macd', title: 'Signal' });
        seriesRefs.current.macdHist = chart.addSeries(HistogramSeries, { priceScaleId: 'macd' });
        chart.priceScale('macd').applyOptions({ scaleMargins: { top, bottom } });
      }
    });

    // ── Infinite scroll ───────────────────────────────────────────────────
    if (onLoadMore) {
      chart.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if (range && range.from < 10 && !loadingMore.current) {
          loadingMore.current = true;
          // oldest timestamp is in data.t[0] — retrieved via ref to avoid stale closure
          const oldest = seriesRefs.current._oldestTs;
          if (oldest) onLoadMore(oldest);
        }
      });
    }

    // ── Resize ────────────────────────────────────────────────────────────
    const onResize = () => {
      if (chartRef.current && containerRef.current)
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
      seriesRefs.current = {};
    };
  }, [height, showVolume, showEMA, showRSI, showMACD, onLoadMore]); // eslint-disable-line

  // ── Effect 2 — push data into existing series (no chart rebuild) ──────────
  useEffect(() => {
    if (!data?.t?.length) return;
    const s = seriesRefs.current;
    if (!s.candles) return;           // chart not created yet — will run again once it is

    const { t: times, o, h, l, c, v } = data;

    // Store oldest timestamp for infinite-scroll callback
    s._oldestTs = times[0];

    // Save visible range so prepending data doesn't jump the view
    const chart       = chartRef.current;
    const savedRange  = initialLoad.current ? null : chart?.timeScale().getVisibleLogicalRange();

    // Candles
    s.candles.setData(times.map((t, i) => ({ time: t, open: o[i], high: h[i], low: l[i], close: c[i] })));

    // Volume
    if (s.vol) {
      s.vol.setData(times.map((t, i) => ({
        time: t, value: v[i],
        color: c[i] >= o[i] ? '#26a69a66' : '#ef535066',
      })));
    }

    // EMAs
    if (s.ema20) {
      const e20  = calcEMA(c, 20);
      const e50  = calcEMA(c, 50);
      const e200 = calcEMA(c, 200);
      s.ema20 .setData(times.map((t, i) => ({ time: t, value: e20[i]  })).filter(d => d.value !== null));
      s.ema50 .setData(times.map((t, i) => ({ time: t, value: e50[i]  })).filter(d => d.value !== null));
      s.ema200.setData(times.map((t, i) => ({ time: t, value: e200[i] })).filter(d => d.value !== null));
    }

    // RSI
    if (s.rsi) {
      const rsi = calcRSI(c, 14);
      s.rsi  .setData(times.map((t, i) => ({ time: t, value: rsi[i] })).filter(d => d.value !== null));
      s.rsiOB.setData(times.map(t => ({ time: t, value: 70 })));
      s.rsiOS.setData(times.map(t => ({ time: t, value: 30 })));
    }

    // MACD
    if (s.macdLine) {
      const { macd, signal: sig, hist } = calcMACD(c);
      s.macdLine.setData(times.map((t, i) => ({ time: t, value: macd[i] })).filter(d => d.value !== null));
      s.macdSig .setData(times.map((t, i) => ({ time: t, value: sig[i]  })).filter(d => d.value !== null));
      s.macdHist.setData(times.map((t, i) => ({
        time: t, value: hist[i],
        color: (hist[i] ?? 0) >= 0 ? '#26a69a' : '#ef5350',
      })).filter(d => d.value !== null));
    }

    if (initialLoad.current) {
      chart?.timeScale().fitContent();
      initialLoad.current = false;
    } else if (savedRange) {
      // Restore position after prepending older bars
      chart?.timeScale().setVisibleLogicalRange(savedRange);
    }
  }, [data]);

  if (!data?.t?.length) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#131722', color: '#666', borderRadius: 4, border: '1px solid #2a2e39',
      }}>
        No chart data
      </div>
    );
  }

  return (
    <div ref={containerRef}
      style={{ width: '100%', height, borderRadius: 4, overflow: 'hidden', border: '1px solid #2a2e39' }}
    />
  );
}
